import React from 'react';
import { Navigate, useParams, useLocation } from 'react-router-dom';
import { useSession } from '../../context/SessionContext';
import { useNavigation } from '../../hooks/useNavigation';

function ProtectedRoute({ children, allowWithoutSession = false }) {
  const { validateSession, isLoading, sessionData } = useSession();
  const { getCurrentStep, isStepAccessible, getAppropriateStep, isChallengeDone } = useNavigation();
  const params = useParams();
  const location = useLocation();
  
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-blue-500 border-r-transparent mb-4"></div>
          <p className="text-xl">Loading...</p>
        </div>
      </div>
    );
  }
  
  const eid = params.eid;
  
  // Session validation
  if (!allowWithoutSession && !validateSession()) {
    return <Navigate to={eid ? `/${eid}` : '/'} replace />;
  }
  
  // COMMENT THIS OUT TO let users access pages where they'll see AbandonedScreen component
  // Handle abandoned evaluations -> redirect to WelcomeScreen where they will see the "abandoned" message
  if (sessionData && 
      (sessionData.challenge_status === "cs_abandoned" || 
       sessionData.questionnaire_status === "qs_abandoned")) {
    // Allow access to welcome screen (both / and /:eid routes)
    const isWelcomePage = location.pathname === '/' || location.pathname === `/${eid}`;
    if (!isWelcomePage) {
      return <Navigate to={eid ? `/${eid}` : '/'} replace />;
    }
  }
  
  // Auto-redirect to finished if both complete (but allow access to welcome screen)
  if (sessionData) {
    // QUESTIONNAIRE SKIPPING: Check questionnaire status only if the questionnaire step is enabled
    const questionnaireComplete = !sessionData?.steps?.step_questionnaire || 
                                  sessionData.questionnaire_status === "qs_done";
    
    if (questionnaireComplete && isChallengeDone(sessionData.challenge_status)) {
      const isWelcomePage = location.pathname === '/' || location.pathname === `/${eid}`;
      if (!location.pathname.includes('/finished') && !isWelcomePage) {
        return <Navigate to={`/finished/${eid}`} replace />;
      }
    }
  }
  
  // Check step accessibility
  if (sessionData) {
    const currentStep = getCurrentStep(location.pathname);
    
    if (currentStep && !isStepAccessible(currentStep.key)) {
      const appropriateStep = getAppropriateStep();
      const redirectPath = appropriateStep.path === '/' ? `/${eid}` : `${appropriateStep.path}/${eid}`;
      return <Navigate to={redirectPath} replace />;
    }
  }

  return children;
}

export default ProtectedRoute;
